import { useState, useMemo, useCallback, type CSSProperties } from 'react';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { qPatientForms, type PatientFormRow } from '@helios/data-access';
import { formatDateShort, type PatientFormItem } from './consultation-data';

interface FormsTabProps {
  patientId: string;
}

type FormFilter = 'all' | 'consent' | 'lab' | 'other';

export function FormsTab({ patientId }: FormsTabProps) {
  const { db } = usePowerSyncDb();

  const { data: formRows = [] } = usePowerSyncQuery<PatientFormRow>(qPatientForms(patientId));

  const forms: PatientFormItem[] = useMemo(
    () =>
      formRows.map((r) => ({
        id: r.id,
        patientId: r.patient_id ?? '',
        name: r.name ?? '',
        tag: (r.tag ?? 'other') as PatientFormItem['tag'],
        createdAt: r.created_at ?? '',
      })),
    [formRows],
  );

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FormFilter>('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = forms;
    if (filter !== 'all') {
      result = result.filter((f) => f.tag === filter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((f) => f.name.toLowerCase().includes(q));
    }
    return result;
  }, [forms, search, filter]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_forms WHERE id = ?`, [id]);
      setOpenMenu(null);
    },
    [db],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Filter */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as FormFilter)}
          style={selectStyle}
        >
          <option value="all">All</option>
          <option value="consent">Consent</option>
          <option value="lab">Lab</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sp-12)',
            color: 'var(--text-muted)',
            gap: 'var(--sp-3)',
          }}
        >
          <NavIcon name="file-text" />
          <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semi)' }}>
            No forms found
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>
            {forms.length === 0
              ? 'No forms have been created yet'
              : 'Try adjusting your search or filter'}
          </span>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['FORM NAME', 'CATEGORY', 'DATE', 'ACTIONS'].map((col) => (
                  <th key={col} style={thStyle}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((form) => (
                <tr key={form.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 'var(--radius-md)',
                          background: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-muted)',
                          flexShrink: 0,
                        }}
                      >
                        <NavIcon name="file-text" />
                      </div>
                      <span
                        style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}
                      >
                        {form.name}
                      </span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <FormTagPill tag={form.tag} />
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                    {formatDateShort(form.createdAt)}
                  </td>
                  <td style={{ ...tdStyle, position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setOpenMenu(openMenu === form.id ? null : form.id)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: openMenu === form.id ? 'var(--bg-surface-2)' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-muted)',
                        padding: 0,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>

                    {openMenu === form.id && (
                      <>
                        <div
                          style={{ position: 'fixed', inset: 0, zIndex: 9 }}
                          onClick={() => setOpenMenu(null)}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            right: 'var(--sp-4)',
                            top: '100%',
                            marginTop: -4,
                            width: 150,
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 10,
                            overflow: 'hidden',
                          }}
                        >
                          {['View', 'Download', 'Delete'].map((action) => (
                            <button
                              key={action}
                              type="button"
                              onClick={() => {
                                if (action === 'Delete') handleDelete(form.id);
                                else setOpenMenu(null);
                              }}
                              style={{
                                width: '100%',
                                padding: 'var(--sp-2) var(--sp-3)',
                                background: 'none',
                                border: 'none',
                                textAlign: 'left',
                                fontSize: 'var(--font-size-sm)',
                                color: action === 'Delete' ? '#dc2626' : 'var(--text)',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => {
                                (e.target as HTMLElement).style.background = 'var(--bg-surface-2)';
                              }}
                              onMouseLeave={(e) => {
                                (e.target as HTMLElement).style.background = 'none';
                              }}
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tag Pill ───

function FormTagPill({ tag }: { tag: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    consent: { bg: '#dbeafe', text: '#1d4ed8' },
    lab: { bg: '#fef3c7', text: '#92400e' },
    other: { bg: '#f1f5f9', text: '#475569' },
  };
  const c = colors[tag] ?? colors.other;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.text,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
        textTransform: 'capitalize',
      }}
    >
      {tag}
    </span>
  );
}

// ─── Styles ───

const thStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  textAlign: 'left',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};
